import { useCotizacionStore } from '../../../Providers/CotizacionStore';
import { BffError } from './bffError';

/**
 * Sesión de cotización del camino nuevo: el JWT que emite `POST contacto` y
 * los ids opacos asociados. Vive en el `CotizacionStore` (sessionStorage), que
 * ya persiste el resto del funnel.
 *
 * `contactoId` se reusa como `prospectoId`: el resto del front ya lo lee con
 * ese nombre y así el camino viejo y el nuevo comparten pantallas.
 */

export interface SesionCotizacion {
  token: string;
  expiraEn?: string;
  prospectoId?: string;
  cotizacionId?: string;
}

export const guardarSesionCotizacion = (sesion: SesionCotizacion): void => {
  const { cotizacionValues, setCotizacionValues } =
    useCotizacionStore.getState();
  setCotizacionValues({
    ...cotizacionValues,
    token: sesion.token,
    expiraEn: sesion.expiraEn,
    contactoId: sesion.prospectoId ?? cotizacionValues.contactoId,
    cotizacionId: sesion.cotizacionId ?? cotizacionValues.cotizacionId,
  });
};

export const guardarCotizacionId = (cotizacionId: string): void => {
  const { cotizacionValues, setCotizacionValues } =
    useCotizacionStore.getState();
  if (cotizacionValues.cotizacionId === cotizacionId) return;
  setCotizacionValues({ ...cotizacionValues, cotizacionId });
};

export const leerSesionCotizacion = (): SesionCotizacion => {
  const { cotizacionValues } = useCotizacionStore.getState();
  return {
    token: cotizacionValues.token || '',
    expiraEn: cotizacionValues.expiraEn,
    prospectoId: cotizacionValues.contactoId,
    cotizacionId: cotizacionValues.cotizacionId,
  };
};

/** true si el JWT ya venció según `expiraEn` (evita un round trip para un 401). */
export const tokenVencido = (expiraEn?: string): boolean => {
  if (!expiraEn) return false;
  const vencimiento = Date.parse(expiraEn);
  if (Number.isNaN(vencimiento)) return false;
  return vencimiento <= Date.now();
};

/**
 * Token para una llamada autenticada. Tira `quote_token_expired` en vez de
 * pegarle al BFF sin token o con uno vencido: el front lo trata igual que el
 * 401 y manda a rehacer el paso de datos.
 */
export const exigirToken = (): string => {
  const { token, expiraEn } = leerSesionCotizacion();
  if (!token) throw new BffError('quote_token_invalid', 401);
  if (tokenVencido(expiraEn)) throw new BffError('quote_token_expired', 401);
  return token;
};
